import { FC, useContext } from "react";
import { Link , useLocation } from "react-router-dom";
import {
  ChevronRight,
  Home,
  Settings,
  Database,
  FileText,
  Clock,
  BarChart2,
  Menu,
  X,
  Check,
  AlertCircle,
  HelpCircle,
  ScrollText
} from "lucide-react";
import { SidebarProps } from "../../types/types";
import { RoleConstants } from "../common/utilities/appConstants";
import GlobalAuthenticatedContext from "../common/contexts/GlobalAuthenticatedContexts";

const Sidebar: FC<SidebarProps> = ({
  sidebarOpen,
  toggleSidebar,
 
  setupCompleted,
  navigation = [],
}) => {
  
  const location = useLocation();
  const currentPath = location.pathname;

  const {globalAuthenticated} = useContext(
    GlobalAuthenticatedContext
  );

  const userRoles = globalAuthenticated?.userRoles || [];
  const orgName = globalAuthenticated?.organization?.name || '';
  const usageLiscenseStatus = globalAuthenticated?.usageLiscenseStatus || '';

  // Navigation items
  const navigationItems =
    navigation.length > 0
      ? navigation
      : [
          {
            id: "dashboard",
            name: "Dashboard",
            path: "/",
            icon: "Home",
            requiresSetup: false,
          },
          {
            id: "setup",
            name: "Initial Setup",
            path: "/setup",
            icon: "Settings",
            requiresSetup: false,
            status: setupCompleted ? "completed" : "pending"
          },
          {
            id: "sharepoint",
            name: "SharePoint Config",
            path: "/sharepoint",
            icon: "Database",
            requiresSetup: true,
            adminOnly: true,
          },
          {
            id: "knowledge-base",
            name: "Knowledge Base",
            path: "/knowledge-base",
            icon: "FileText",
            requiresSetup: true,
          },
          {
            id: "trigger-config",
            name: "Trigger Config",
            path: "/trigger-config",
            icon: "Clock",
            requiresSetup: true,
            adminOnly: true,
            liscenseClientOnly: usageLiscenseStatus === 'TRIAL' ? false : true,
          },
          {
            id: "logs",
            name: "Activity Logs",
            path: "/logs",
            icon: "BarChart2",
            requiresSetup: false,
          },
          {
            id: "releasenotes",
            name: "Release Notes",
            path: "/releasenotes",
            icon: "ScrollText",
            requiresSetup: false,
          },
          {
            id: "help",
            name: "Help & Support",
            path: "/help",
            icon: "HelpCircle",
            requiresSetup: false,
          },
        ];

  // Filter navigation items based on role
  const getFilteredNavigationItems = () => {

    const navItems = navigationItems.map((item) => ({
      ...item,
      liscenseClientOnly: item.id === 'trigger-config' && usageLiscenseStatus === 'TRIAL' ? false : true
    }));
    // If no user roles, return only non-admin items
    if (userRoles.length === 0) {
      return navItems.filter((item) => !item.adminOnly);
    }

    // Check if user has admin or sharepointAdmin role
    const hasAdminAccess = userRoles.some(
      (role: any) =>
        role.name === RoleConstants.admin ||
        role.name === RoleConstants.sharepointAdmin
    );

    // Check if user has only sharepointView role
    const hasViewAccess = userRoles.some(
      (role: any) => role.name === RoleConstants.sharepointView
    );

    if (hasAdminAccess) {
      // Return all items for admin users
      return navItems;
    } else if (hasViewAccess) {
      // Return only items that are not admin only
      return navItems.filter((item) => !item.adminOnly);
    }
    // Default: return limited access
    return navItems.filter((item) => !item.adminOnly);
  };

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case "Home":
        return <Home size={20} />;
      case "Settings":
        return <Settings size={20} />;
      case "Database":
        return <Database size={20} />;
      case "FileText":
        return <FileText size={20} />;
      case "Clock":
        return <Clock size={20} />;
      case "BarChart2":
        return <BarChart2 size={20} />;
      case "ScrollText":
        return <ScrollText size={20} />;
      case "HelpCircle":
        return <HelpCircle size={20} />;
      default:
        return <Home size={20} />;
    }
  };

  return (
    <div
      className={`${
        sidebarOpen ? "w-64" : "w-20"
      } bg-slate-800 text-white transition-all duration-300 ease-in-out flex flex-col h-screen fixed`}
    >
      <div className="p-4 flex items-center justify-between border-b border-slate-700">
        {sidebarOpen ? (
          <div className="flex items-center">
            <img
              src="/logo.png"
              alt="KB Connector Logo"
              className="h-8 w-8 mr-2"
            />
            <h1 className="text-xl font-semibold">KB Connector</h1>
          </div>
        ) : (
          <img
            src="/logo.png"
            alt="KB Connector Logo"
            className="h-8 w-8 mx-auto"
          />
        )}
        <button
          onClick={toggleSidebar}
          className="p-2 rounded-md hover:bg-slate-700"
        >
          {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-4">
        <ul>
          {getFilteredNavigationItems().map((item) => {
            const isActive = currentPath === item.path;
          return  (

              <li key={item.id}>
                <Link
                  to={item.path}
                  onClick={(e) => {
                    if (!item.liscenseClientOnly || (item.requiresSetup && !setupCompleted)) {
                      e.preventDefault();
                      return;
                    }
                    // setCurrentPage(item.id);
                  }}
                  className={`w-full flex items-center px-4 py-3 mb-1 ${
                    isActive 
                      ? "bg-slate-700 text-white"
                      : "text-slate-300 hover:bg-slate-700"
                  } ${ !item.liscenseClientOnly ||
                    (item.requiresSetup && !setupCompleted)
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer"
                  } rounded-md transition-colors duration-200`}
                >
                  <span className="mr-3">{getIcon(item.icon)}</span>
                  {sidebarOpen && (
                    <>
                      <span className="flex-1 text-left">{item.name}</span>
                      {item.status === "completed" && (
                        <Check size={16} className="text-green-400" />
                      )}
                      {item.status === "pending" && (
                        <AlertCircle size={16} className="text-yellow-400" />
                      )}
                      {item.status === "error" && (
                        <AlertCircle size={16} className="text-red-400" />
                      )}
                      {isActive && <ChevronRight size={16} />}
                    </>
                  )}
                </Link>
              </li>
            )})}
          
          
        </ul>
      </nav>

      {/* Version footer */}
      <div className="p-4 border-t border-slate-700 flex justify-center">
        {sidebarOpen && (
          <div className="text-sm text-slate-400">
            <p>v1.0.0</p>
            <p>{orgName}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
